(function attachJudesBundleCheckout(root, factory) {
  const api = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.JudesBundleCheckout = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function createJudesBundleCheckout(root) {
  'use strict';

  function normalizeRoot(routesRoot) {
    const value = String(routesRoot || '/');
    return value.endsWith('/') ? value : `${value}/`;
  }

  async function responseJson(response) {
    return response.json().catch(() => ({}));
  }

  async function rollbackAddedLines(fetchFn, routesRoot, addedItems) {
    const keys = (addedItems || []).map((item) => item.key).filter(Boolean);
    const failures = [];

    for (const key of keys) {
      const response = await fetchFn(`${routesRoot}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (!response.ok) failures.push(key);
    }

    return failures;
  }

  function discountIsExplicitlyRejected(cart, discountCode) {
    if (!Array.isArray(cart.discount_codes)) return false;

    const normalizedCode = discountCode.toLowerCase();
    const matching = cart.discount_codes.find((discount) => (
      String(discount.code || '').toLowerCase() === normalizedCode
    ));

    return !matching || matching.applicable === false;
  }

  // Opt-in for the Probier experiment only. Other set checkout contracts stay unchanged.
  function verifyBasket(cart, payload, verification, before, submission) {
    const code = String(verification.code).toLowerCase();
    if (!Array.isArray(cart.discount_codes) || !cart.discount_codes.some((item) =>
      String(item.code || '').toLowerCase() === code && item.applicable === true)) {
      throw new Error('The set discount could not be confirmed');
    }
    if (!Array.isArray(cart.items)) throw new Error('The set basket could not be confirmed');
    const lines = cart.items.filter((item) => item.properties?._judes_probier_submission === submission);
    const quantities = (items) => {
      const result = {};
      for (const item of items) {
        const id = String(item.variant_id || item.id);
        if (!Number.isInteger(Number(item.quantity)) || Number(item.quantity) <= 0) throw new Error('Invalid set basket quantity');
        result[id] = (result[id] || 0) + Number(item.quantity);
      }
      return JSON.stringify(Object.entries(result).sort());
    };
    if (quantities(lines) !== quantities(payload.items)) throw new Error('The set basket does not match your selection');
    const retail = lines.reduce((sum, item) => sum + item.original_line_price, 0);
    const untouched = cart.items.filter((item) => item.properties?._judes_probier_submission !== submission);
    const previous = before.items || [];
    if (quantities(untouched) !== quantities(previous) || (verification.beforeFingerprint && root.JudesProbierCommerce.identity(untouched) !== root.JudesProbierCommerce.identity(previous))) throw new Error('The existing basket changed during checkout');
    // Shopify may allocate this fixed product discount to existing eligible items too.
    // Confirm the customer's whole-cart amount, not an assumed per-line allocation.
    if (retail !== verification.retailValueMinor
      || cart.total_price !== (verification.wholeCartPriceMinor ?? before.total_price + verification.finalPriceMinor)
      || (verification.currency && cart.currency !== verification.currency)) {
      throw new Error('The set price or discount does not match your selection');
    }
    return true;
  }

  async function submit(options) {
    const fetchFn = options.fetchFn;
    const navigate = options.navigate;
    const routesRoot = normalizeRoot(options.routesRoot);
    const discountCode = String(options.discountCode || '').trim();
    const suppliedMessages = options.messages || null;
    const messages = {
      discountMissing: 'The set discount is not configured yet',
      cartEmpty: 'The bundle cart is empty',
      addFailed: 'The set could not be added to your cart',
      discountFailed: 'The set discount could not be applied',
      reviewCart: 'Please review your cart before trying again',
      ...(options.messages || {})
    };

    if (!discountCode) throw new Error(messages.discountMissing);
    if (!options.cartPayload || !Array.isArray(options.cartPayload.items) || options.cartPayload.items.length === 0) {
      throw new Error(messages.cartEmpty);
    }

    let before;
    let submission;
    let cartPayload = options.cartPayload;
    if (options.verification) {
      const v = options.verification;
      if (![v.retailValueMinor, v.finalPriceMinor, v.discountAmountMinor].every((value) => Number.isInteger(value) && value >= 0)
        || v.retailValueMinor - v.discountAmountMinor !== v.finalPriceMinor) throw new Error('Invalid set price verification');
      if (v.beforeCart) {
        before = v.beforeCart;
      } else {
        const beforeResponse = await fetchFn(`${routesRoot}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        before = await responseJson(beforeResponse);
        if (!beforeResponse.ok) throw new Error('The existing basket could not be confirmed');
      }
      if (!before || !Array.isArray(before.items) || !Number.isInteger(before.total_price)) throw new Error('The existing basket could not be confirmed');
      if (v.beforeFingerprint && root.JudesProbierCommerce.basketKey(before) !== v.beforeFingerprint) throw new Error('The cart changed, please review the new total');
      submission = `probier-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      cartPayload = { ...cartPayload, items: cartPayload.items.map((item) => ({ ...item,
        properties: { ...(item.properties || {}), _judes_probier_submission: submission }
      })) };
    }

    const cartResponse = await fetchFn(`${routesRoot}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartPayload)
    });
    const added = await responseJson(cartResponse);

    if (!cartResponse.ok) {
      throw new Error(suppliedMessages ? messages.addFailed : (added.description || messages.addFailed));
    }

    try {
      const discountResponse = await fetchFn(`${routesRoot}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: discountCode })
      });
      const cart = await responseJson(discountResponse);

      if (!discountResponse.ok || discountIsExplicitlyRejected(cart, discountCode)) {
        throw new Error(messages.discountFailed);
      }

      const verified = options.verification
        ? verifyBasket(cart, cartPayload, { ...options.verification, code: discountCode }, before, submission)
        : false;
      if (typeof options.onSuccess === 'function') {
        await options.onSuccess(options.verification ? { added, cart, verified } : { added, cart });
      }
      navigate(`${routesRoot}checkout`);
      return { added, cart, verified };
    } catch (error) {
      let rollbackItems = added.items;
      if (options.verification?.beforeFingerprint) {
        try {
          const response = await fetchFn(`${routesRoot}cart.js`, {credentials:'same-origin',cache:'no-store'});
          const current = await responseJson(response);
          if (!response.ok || !Array.isArray(current.items)) throw Error('Cart unavailable');
          rollbackItems = current.items.filter(item => item.properties?._judes_probier_submission === submission);
        } catch (readError) { rollbackItems = null; }
      }
      const rollbackFailures = rollbackItems ? await rollbackAddedLines(fetchFn, routesRoot, rollbackItems).catch(() => ['network']) : ['unconfirmed'];
      if (before && Array.isArray(before.discount_codes)) {
        try {
          const restored = await fetchFn(`${routesRoot}cart/update.js`, {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: before.discount_codes.map((item) => item.code).join(',') })
          });
          if (!restored.ok) rollbackFailures.push('discount');
        } catch (restoreError) { rollbackFailures.push('discount'); }
      }
      if (rollbackFailures.length > 0) {
        error.message = `${error.message} ${messages.reviewCart}`;
        error.partialCart = true;
      }
      throw error;
    }
  }

  return { submit };
}));
